import { z } from 'zod';

const num = () => z.coerce.number();
const numNull = () => z.union([z.coerce.number(), z.null()]);
const boolish = () => z.union([z.boolean(), z.coerce.number()]);

/* ── MTO Health redesign (2026-05-14) ── */

/* Card 7437 — Orders Summary (last 30 days) — brand grain. */
export const MtoOrdersSummarySchema = z.array(z.object({
  brand_name: z.string().nullable(),
  merch_portfolio: z.string(),
  brand_manager: z.string(),
  brand_poc: z.string().optional().default(''),
  shared_orders: num().optional().default(0),
  orders_shared: num(),
  mto_shared: num(),
  mtc_shared: num(),
  jit_b2b_shared: num().optional().default(0),
  /* JIT (B2B) — added 2026-05-27 via FULL OUTER JOIN onto card 7437. Optional
     so the card can degrade gracefully if the JIT mart is unavailable. */
  jit_shared: num().optional().default(0),
  mto_promised_tat_days: numNull(),
  mto_tat_days: numNull(),
  mtc_promised_tat_days: numNull(),
  mtc_tat_days: numNull(),
  jit_promised_tat_days: numNull().optional(),
  jit_tat_days: numNull().optional(),
  /* JIT delivered / delayed / SLA-scope counts (added 2026-05-27). Used to
     fold JIT into the page's Delivered / Delayed / % Delayed columns when
     the user includes JIT in the Fulfilment Mode filter. */
  jit_delivered: num().optional().default(0),
  jit_delayed: num().optional().default(0),
  jit_sla_scope: num().optional().default(0),
  jit_avg_delay_days: numNull().optional(),
  jit_p75_delay_days: numNull().optional(),
  orders_declined: num(),
  mto_declined: num().optional().default(0),
  mtc_declined: num().optional().default(0),
  pct_declined: num(),
  avg_days_to_decline: numNull(),
  p75_days_to_decline: numNull(),
  /* SLA scope — denominator for % delayed (aligned to Brand Performance Summary). */
  sla_scope_orders: num().optional(),
  orders_delivered: num(),
  orders_delayed: num(),
  pct_delayed: num(),
  avg_days_of_delay: numNull(),
  p75_days_of_delay: numNull(),
  returns: num(),
  dispatch_scope_orders: num().optional().default(0),
  dispatch_on_time_orders: num().optional().default(0),
  dispatch_late_orders: num().optional().default(0),
  dispatch_on_time_pct: numNull().optional(),
  dispatch_late_pct: numNull().optional(),
  avg_dispatch_late_days: numNull().optional(),
  p75_dispatch_late_days: numNull().optional(),
  warehouse_arrival_scope_orders: num().optional().default(0),
  warehouse_arrival_on_time_orders: num().optional().default(0),
  warehouse_arrival_late_orders: num().optional().default(0),
  warehouse_arrival_on_time_pct: numNull().optional(),
  warehouse_arrival_late_pct: numNull().optional(),
  avg_warehouse_arrival_late_days: numNull().optional(),
  p75_warehouse_arrival_late_days: numNull().optional(),
  penalty_cutoff_scope_orders: num().optional().default(0),
  received_by_penalty_cutoff_orders: num().optional().default(0),
  penalty_cutoff_missed_orders: num().optional().default(0),
  received_by_penalty_cutoff_pct: numNull().optional(),
  penalty_cutoff_missed_pct: numNull().optional(),
  total_issue_signals: num().optional().default(0),
  dominant_issue_clock: z.string().optional().default('NONE'),
  recommended_action: z.string().optional().default(''),
  score_mtoc_lines: num().optional().default(0),
  score_declined_lines: num().optional().default(0),
  score_wh_delay_1d_scope_lines: num().optional().default(0),
  score_wh_delay_1d_lines: num().optional().default(0),
  score_wh_delay_7d_scope_lines: num().optional().default(0),
  score_wh_delay_7d_lines: num().optional().default(0),
  score_delay_cancelled_lines: num().optional().default(0),
  score_wh_delayed_without_timely_revision_lines: numNull().optional(),
  score_total_customers: num().optional().default(0),
  score_impacted_customers: num().optional().default(0),
  score_avg_days_to_decline: numNull().optional(),
  clock_maturity_pct: numNull().optional(),
  score_maturity_bucket: z.string().optional().default('NO_LINES'),
  brand_health_score: numNull().optional(),
  brand_health_band: z.string().optional().default('LOW_SAMPLE'),
  score_wh_delay_1d_pct: numNull().optional(),
  score_wh_delay_7d_pct: numNull().optional(),
  impacted_customer_pct: numNull().optional(),
  delay_cancel_pct: numNull().optional(),
  delayed_without_timely_revision_pct: numNull().optional(),
  score_decline_pct: numNull().optional(),
  dominant_score_issue: z.string().optional().default('NO_CURRENT_ISSUE'),
  score_recommended_action: z.string().optional().default(''),
}));
export type MtoOrdersSummary = z.infer<typeof MtoOrdersSummarySchema>;

/* Card 7438 — Live Orders (current state) — brand grain. Per-mode counts
   let the frontend scope by Fulfilment Mode filter; legacy aggregates kept
   for backwards compatibility. */
export const MtoLiveOrdersSchema = z.array(z.object({
  brand_name: z.string().nullable(),
  merch_portfolio: z.string(),
  brand_manager: z.string(),
  brand_poc: z.string().optional().default(''),
  live_orders: num(),
  live_overdue: num(),
  live_overdue_5plus: num(),
  avg_open_overdue_days: numNull(),
  current_open_lines: num().optional().default(0),
  brand_vendor_pending_lines: num().optional().default(0),
  inbound_to_lk_pending_lines: num().optional().default(0),
  wh_qc_grn_recon_pending_lines: num().optional().default(0),
  dispatch_overdue_lines: num().optional().default(0),
  warehouse_arrival_overdue_lines: num().optional().default(0),
  action_overdue_lines: num().optional().default(0),
  action_overdue_7d_lines: num().optional().default(0),
  action_overdue_10d_lines: num().optional().default(0),
  dispatch_7d_overdue_lines: num().optional().default(0),
  dispatch_10d_overdue_lines: num().optional().default(0),
  warehouse_arrival_7d_overdue_lines: num().optional().default(0),
  warehouse_arrival_10d_overdue_lines: num().optional().default(0),
  warehouse_due_missing_lines: num().optional().default(0),
  max_open_overdue_days: numNull().optional(),
  affected_vendors: num().optional().default(0),
  recommended_action: z.string().optional().default(''),
  mto_live: num().optional().default(0),
  mtc_live: num().optional().default(0),
  jit_live: num().optional().default(0),
  mto_live_overdue: num().optional().default(0),
  mtc_live_overdue: num().optional().default(0),
  jit_live_overdue: num().optional().default(0),
  mto_live_overdue_5plus: num().optional().default(0),
  mtc_live_overdue_5plus: num().optional().default(0),
  jit_live_overdue_5plus: num().optional().default(0),
  mtomtc_avg_open_overdue_days: numNull().optional(),
  jit_avg_open_overdue_days: numNull().optional(),
}));
export type MtoLiveOrders = z.infer<typeof MtoLiveOrdersSchema>;

/* Card 7439 — Weekly Trend (12 weeks) — brand × week grain. */
export const MtoWeeklyTrendSchema = z.array(z.object({
  week_start: z.string(),
  brand_name: z.string().nullable().optional(),
  merch_portfolio: z.string().optional().default(''),
  brand_manager: z.string().optional().default(''),
  shared: num(),
  delivered: num(),
  declined: num(),
  delayed: num(),
  delayed_5plus: num(),
  jit_b2b_shared: num().optional().default(0),
  dispatch_scope_orders: num().optional().default(0),
  dispatch_on_time_orders: num().optional().default(0),
  dispatch_late_orders: num().optional().default(0),
  dispatch_on_time_pct: numNull().optional(),
  dispatch_late_pct: numNull().optional(),
  warehouse_arrival_scope_orders: num().optional().default(0),
  warehouse_arrival_on_time_orders: num().optional().default(0),
  warehouse_arrival_late_orders: num().optional().default(0),
  warehouse_arrival_on_time_pct: numNull().optional(),
  warehouse_arrival_late_pct: numNull().optional(),
  penalty_cutoff_scope_orders: num().optional().default(0),
  received_by_penalty_cutoff_orders: num().optional().default(0),
  penalty_cutoff_missed_orders: num().optional().default(0),
  received_by_penalty_cutoff_pct: numNull().optional(),
  penalty_cutoff_missed_pct: numNull().optional(),
  mto_shared: num().optional().default(0),
  mtc_shared: num().optional().default(0),
  jit_shared: num().optional().default(0),
  mto_delivered: num().optional().default(0),
  mtc_delivered: num().optional().default(0),
  jit_delivered: num().optional().default(0),
  mto_declined: num().optional().default(0),
  mtc_declined: num().optional().default(0),
  mto_delayed: num().optional().default(0),
  mtc_delayed: num().optional().default(0),
  jit_delayed: num().optional().default(0),
  mto_delayed_5plus: num().optional().default(0),
  mtc_delayed_5plus: num().optional().default(0),
  jit_delayed_5plus: num().optional().default(0),
}));
export type MtoWeeklyTrend = z.infer<typeof MtoWeeklyTrendSchema>;

/* Card 7440 — Delay Histogram (last 30 days, closed orders) — brand × bin grain. */
export const MtoDelayHistogramSchema = z.array(z.object({
  brand_name: z.string().nullable(),
  merch_portfolio: z.string(),
  brand_manager: z.string(),
  issue_clock: z.string().optional().default('WAREHOUSE_ARRIVAL'),
  delay_bin_label: z.string(),
  bin_order: num(),
  orders_count: num(),
  dispatch_count: num().optional().default(0),
  warehouse_arrival_count: num().optional().default(0),
  penalty_cutoff_count: num().optional().default(0),
  mto_count: num().optional().default(0),
  mtc_count: num().optional().default(0),
  jit_count: num().optional().default(0),
}));
export type MtoDelayHistogram = z.infer<typeof MtoDelayHistogramSchema>;

/* Card 7443 — Delay Detail Orders (last 30 days, closed only) — order-line grain.
   Backs the click-through modal on the Delay Histogram. Lazy-fetched. */
export const MtoDelayDetailOrdersSchema = z.array(z.object({
  order_line_item_id: num(),
  /* Internal customer-order ID — drives the /orders/show/:id link. Optional
     while the card change propagates. */
  order_id: numNull().optional(),
  sales_channel_order_id: z.string().nullable(),
  brand_name: z.string().nullable(),
  merch_portfolio: z.string(),
  brand_manager: z.string(),
  vendor_name: z.string(),
  vendor_id: numNull().optional(),
  unicom_purchase_order_code: z.string().nullable().optional(),
  sku_code: z.string(),
  vendor_sku_code: z.string().nullable().optional(),
  product_name: z.string(),
  fulfillment_bucket: z.string(),
  issue_clock: z.string().optional().default('WAREHOUSE_ARRIVAL'),
  placed_date_str: z.string().nullable(),
  po_shared_date_str: z.string().nullable(),
  effective_due_date_str: z.string().nullable(),
  due_date_str: z.string().nullable().optional(),
  actual_date_str: z.string().nullable().optional(),
  actual_source: z.string().nullable().optional(),
  grn_date_str: z.string().nullable(),
  vendor_lag_days: numNull(),
  lag_days: numNull().optional(),
  recommended_action: z.string().nullable().optional(),
  current_vendor_dispatch_by_date_str: z.string().nullable().optional(),
  warehouse_arrival_due_date_str: z.string().nullable().optional(),
  penalty_cutoff_date_str: z.string().nullable().optional(),
  brand_dispatch_courier_partner_name: z.string().nullable().optional(),
  brand_dispatch_courier_tracking_id: z.string().nullable().optional(),
  brand_dispatch_courier_tracking_link: z.string().nullable().optional(),
  vendor_dispatch_courier_partner_name: z.string().nullable().optional(),
  vendor_dispatch_courier_tracking_id: z.string().nullable().optional(),
  vendor_dispatch_courier_tracking_link: z.string().nullable().optional(),
  fulfillment_penalty_status: z.string().nullable().optional(),
  fulfillment_penalty_amount: numNull().optional(),
  warehouse_due_repair_class: z.string().nullable().optional(),
  delay_bin_label: z.string(),
  bin_order: num(),
}));
export type MtoDelayDetailOrders = z.infer<typeof MtoDelayDetailOrdersSchema>;

/* Card 7444 — Live Orders Detail (currently open POs) — order-line grain.
   Backs the click-through modal on the Live Orders table. Lazy-fetched. */
export const MtoLiveOrdersDetailSchema = z.array(z.object({
  order_line_item_id: num(),
  order_id: numNull().optional(),
  sales_channel_order_id: z.string().nullable(),
  brand_name: z.string().nullable(),
  merch_portfolio: z.string(),
  brand_manager: z.string(),
  vendor_name: z.string(),
  vendor_id: numNull().optional(),
  unicom_purchase_order_code: z.string().nullable().optional(),
  sku_code: z.string(),
  vendor_sku_code: z.string().nullable().optional(),
  product_name: z.string(),
  fulfillment_bucket: z.string(),
  placed_date_str: z.string().nullable(),
  po_shared_date_str: z.string().nullable(),
  effective_due_date_str: z.string().nullable(),
  vendor_lag_days_open: numNull(),
  warehouse_arrival_lag_days_open: numNull().optional(),
  brand_poc: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  brand_status: z.string().nullable().optional(),
  ops_lane_v2: z.string().nullable().optional(),
  owner_queue: z.string().nullable().optional(),
  urgency_bucket: z.string().nullable().optional(),
  recommended_action: z.string().nullable().optional(),
  dispatch_overdue_flag: num().optional().default(0),
  warehouse_arrival_overdue_flag: num().optional().default(0),
  current_vendor_dispatch_by_date_str: z.string().nullable().optional(),
  warehouse_arrival_due_date_str: z.string().nullable().optional(),
  penalty_cutoff_date_str: z.string().nullable().optional(),
  effective_brand_dispatched_on_str: z.string().nullable().optional(),
  brand_status_received_at_warehouse_on_str: z.string().nullable().optional(),
  brand_status_qc_passed_on_str: z.string().nullable().optional(),
  brand_status_qc_failed_on_str: z.string().nullable().optional(),
  brand_dispatch_courier_partner_name: z.string().nullable().optional(),
  brand_dispatch_courier_tracking_id: z.string().nullable().optional(),
  brand_dispatch_courier_tracking_link: z.string().nullable().optional(),
  vendor_dispatch_courier_partner_name: z.string().nullable().optional(),
  vendor_dispatch_courier_tracking_id: z.string().nullable().optional(),
  vendor_dispatch_courier_tracking_link: z.string().nullable().optional(),
  /* Forward-compatible: present on some card revisions; lets admin show whether
     the brand disabled MTO for the style on the vendor side. */
  disable_mto_for_this_style: z.union([z.coerce.number(), z.boolean(), z.null()]).optional(),
  is_overdue: num(),
  is_overdue_5plus: num(),
  warehouse_due_missing_flag: num().optional().default(0),
  warehouse_due_repair_class: z.string().nullable().optional(),
  fulfillment_penalty_status: z.string().nullable().optional(),
  fulfillment_penalty_amount: numNull().optional(),
}));
export type MtoLiveOrdersDetail = z.infer<typeof MtoLiveOrdersDetailSchema>;

/* Cards 7758-7762 — Merch-head weekly accountability. These cards intentionally
   consume the same score components as Dashboard 195 and use brand_manager as
   the current merch-head proxy until a separate merch hierarchy exists. */
export const MtoMerchHeadWeeklyLeaderboardSchema = z.array(z.object({
  metric_week: z.string(),
  merch_head: z.string(),
  primary_merch_portfolio: z.string(),
  merch_head_weekly_health_score: numNull(),
  brand_mix_expected_score: numNull(),
  brand_mix_adjusted_delta: numNull(),
  merch_head_health_band: z.string(),
  dominant_score_issue: z.string(),
  total_orders_shared: num(),
  total_customers: num().optional().default(0),
  impacted_customers: num().optional().default(0),
  impacted_customer_pct: numNull().optional(),
  brands_count: num(),
  mto_shared: num(),
  mtc_shared: num(),
  jit_b2b_shared: num(),
  clock_maturity_pct: numNull(),
  customer_due_scope_lines: num(),
  customer_late_lines: num(),
  customer_late_pct: numNull(),
  dispatch_scope_lines: num(),
  dispatch_late_lines: num(),
  dispatch_late_pct: numNull(),
  warehouse_arrival_scope_lines: num(),
  warehouse_arrival_late_lines: num(),
  warehouse_arrival_late_pct: numNull(),
  penalty_cutoff_scope_lines: num(),
  penalty_cutoff_missed_lines: num(),
  penalty_cutoff_missed_pct: numNull(),
  delayed_lines: num(),
  delayed_without_timely_revision_lines: num(),
  delayed_without_timely_revision_pct: numNull(),
  declined_lines: num(),
  decline_pct: numNull(),
  no_action_lines: num(),
  no_action_pct: numNull(),
  wh_delay_1d_scope_lines: num().optional().default(0),
  wh_delay_1d_lines: num().optional().default(0),
  wh_delay_1d_pct: numNull().optional(),
  wh_delay_7d_scope_lines: num().optional().default(0),
  wh_delay_7d_lines: num().optional().default(0),
  wh_delay_7d_pct: numNull().optional(),
  delay_cancelled_lines: num().optional().default(0),
  delay_cancel_pct: numNull().optional(),
  avg_days_to_decline: numNull().optional(),
  score_delay_no_revision_pct: numNull().optional(),
  expected_score_covered_lines: numNull(),
  current_open_lines: num(),
  live_dispatch_overdue_lines: num(),
  live_warehouse_arrival_overdue_lines: num(),
  live_10d_action_overdue_lines: num(),
  weekly_intervention_rank: num(),
  weekly_adjusted_rank: num(),
}));
export type MtoMerchHeadWeeklyLeaderboard = z.infer<typeof MtoMerchHeadWeeklyLeaderboardSchema>;

/* Cards 7763-7764 — Admin selected-window merch accountability. These are
   recomputed at owner grain from raw line counts over the Admin date-picker
   window and compared with the immediately previous same-length window. */
export const MtoMerchOwnerWindowComparisonSchema = z.array(z.object({
  current_window_start_date: z.string(),
  current_window_end_date: z.string(),
  previous_window_start_date: z.string(),
  previous_window_end_date: z.string(),
  owner_type: z.enum(['MERCH_HEAD', 'PORTFOLIO']),
  owner_name: z.string(),
  merch_head: z.string().nullable(),
  primary_merch_portfolio: z.string(),
  merch_heads_count: num(),
  brands_count: num(),
  previous_brands_count: num().optional().default(0),
  total_orders_shared: num(),
  previous_total_orders_shared: num().optional().default(0),
  total_customers: num().optional().default(0),
  impacted_customers: num().optional().default(0),
  impacted_customer_pct: numNull().optional(),
  mto_shared: num(),
  mtc_shared: num(),
  jit_b2b_shared: num(),
  clock_maturity_pct: numNull(),
  previous_clock_maturity_pct: numNull().optional(),
  owner_health_score: numNull(),
  previous_owner_health_score: numNull().optional(),
  score_delta: numNull().optional(),
  owner_health_band: z.string(),
  dominant_score_issue: z.string(),
  wh_delay_1d_scope_lines: num().optional().default(0),
  wh_delay_1d_lines: num().optional().default(0),
  wh_delay_1d_pct: numNull().optional(),
  wh_delay_7d_scope_lines: num().optional().default(0),
  wh_delay_7d_lines: num().optional().default(0),
  wh_delay_7d_pct: numNull().optional(),
  delay_cancelled_lines: num().optional().default(0),
  delay_cancel_pct: numNull().optional(),
  avg_days_to_decline: numNull().optional(),
  delayed_without_timely_revision_lines: numNull().optional(),
  delayed_without_timely_revision_pct: numNull(),
  declined_lines: num(),
  decline_pct: numNull(),
  current_open_lines: num(),
  live_dispatch_overdue_lines: num(),
  live_warehouse_arrival_overdue_lines: num(),
  live_10d_action_overdue_lines: num(),
  intervention_rank: num(),
}));
export type MtoMerchOwnerWindowComparison = z.infer<typeof MtoMerchOwnerWindowComparisonSchema>;

export const MtoMerchHeadWeeklyTrendSchema = z.array(z.object({
  metric_week: z.string(),
  merch_head: z.string(),
  primary_merch_portfolio: z.string(),
  brands_count: num(),
  total_orders_shared: num(),
  clock_maturity_pct: numNull(),
  merch_head_weekly_health_score: numNull(),
  brand_mix_expected_score: numNull(),
  brand_mix_adjusted_delta: numNull(),
  customer_late_pct: numNull(),
  dispatch_late_pct: numNull(),
  warehouse_arrival_late_pct: numNull(),
  delayed_without_timely_revision_pct: numNull(),
  decline_pct: numNull(),
  no_action_pct: numNull(),
  wh_delay_1d_pct: numNull().optional(),
  wh_delay_7d_pct: numNull().optional(),
  impacted_customer_pct: numNull().optional(),
  delay_cancel_pct: numNull().optional(),
  avg_days_to_decline: numNull().optional(),
  score_delay_no_revision_pct: numNull().optional(),
  merch_head_health_band: z.string(),
  previous_week_score: numNull(),
  score_wow_delta: numNull(),
  rolling_4w_score: numNull(),
}));
export type MtoMerchHeadWeeklyTrend = z.infer<typeof MtoMerchHeadWeeklyTrendSchema>;

export const MtoMerchHeadBrandContributionSchema = z.array(z.object({
  metric_week: z.string(),
  merch_head: z.string(),
  primary_merch_portfolio: z.string(),
  brand_name: z.string().nullable(),
  brand_weekly_health_score: numNull(),
  brand_health_band: z.string(),
  total_orders_shared: num(),
  total_customers: num().optional().default(0),
  impacted_customers: num().optional().default(0),
  impacted_customer_pct: numNull().optional(),
  mto_shared: num(),
  mtc_shared: num(),
  jit_b2b_shared: num(),
  merch_head_volume_share_pct: numNull(),
  score_drag_contribution: numNull(),
  clock_maturity_pct: numNull(),
  customer_due_scope_lines: num(),
  customer_late_lines: num(),
  customer_late_pct: numNull(),
  dispatch_scope_lines: num(),
  dispatch_late_lines: num(),
  dispatch_late_pct: numNull(),
  warehouse_arrival_scope_lines: num(),
  warehouse_arrival_late_lines: num(),
  warehouse_arrival_late_pct: numNull(),
  penalty_cutoff_scope_lines: num(),
  penalty_cutoff_missed_lines: num(),
  penalty_cutoff_missed_pct: numNull(),
  delayed_lines: num(),
  delayed_without_timely_revision_lines: num(),
  delayed_without_timely_revision_pct: numNull(),
  declined_lines: num(),
  decline_pct: numNull(),
  no_action_lines: num(),
  no_action_pct: numNull(),
  wh_delay_1d_scope_lines: num().optional().default(0),
  wh_delay_1d_lines: num().optional().default(0),
  wh_delay_1d_pct: numNull().optional(),
  wh_delay_7d_scope_lines: num().optional().default(0),
  wh_delay_7d_lines: num().optional().default(0),
  wh_delay_7d_pct: numNull().optional(),
  delay_cancelled_lines: num().optional().default(0),
  delay_cancel_pct: numNull().optional(),
  avg_days_to_decline: numNull().optional(),
  score_delay_no_revision_pct: numNull().optional(),
}));
export type MtoMerchHeadBrandContribution = z.infer<typeof MtoMerchHeadBrandContributionSchema>;

export const MtoMerchHeadIssueBreakdownSchema = z.array(z.object({
  metric_week: z.string(),
  merch_head: z.string(),
  primary_merch_portfolio: z.string(),
  issue_type: z.string(),
  issue_lines: num(),
  affected_brands: num(),
  sample_brands: z.string().nullable(),
  recommended_action: z.string(),
}));
export type MtoMerchHeadIssueBreakdown = z.infer<typeof MtoMerchHeadIssueBreakdownSchema>;

export const MtoMerchHeadRawIssueLinesSchema = z.array(z.object({
  metric_week: z.string(),
  merch_head: z.string(),
  primary_merch_portfolio: z.string(),
  brand_name: z.string().nullable(),
  issue_type: z.string(),
  order_line_item_id: num(),
  sales_channel_order_id: z.string().nullable(),
  vendor_id: numNull(),
  vendor_name: z.string().nullable(),
  unicom_purchase_order_code: z.string().nullable(),
  sku_code: z.string().nullable(),
  vendor_sku_code: z.string().nullable(),
  product_name: z.string().nullable(),
  vendor_managed_fulfillment_bucket: z.string().nullable(),
  is_jit_b2b: boolish(),
  status: z.string().nullable(),
  brand_status: z.string().nullable(),
  placed_date_str: z.string().nullable(),
  po_shared_date_str: z.string().nullable(),
  customer_promise_due_date_str: z.string().nullable(),
  customer_lag_days: numNull(),
  dispatch_operational_due_date_str: z.string().nullable(),
  dispatch_actual_date_str: z.string().nullable(),
  warehouse_arrival_due_date_str: z.string().nullable(),
  warehouse_arrival_actual_date_str: z.string().nullable(),
  customer_delivered_date_str: z.string().nullable().optional(),
  customer_delivered_on_str: z.string().nullable().optional(),
  package_customer_delivered_on: z.string().nullable().optional(),
  warehouse_arrival_actual_source: z.string().nullable(),
  score_wh_reach_lag_days: numNull().optional(),
  penalty_cutoff_date_str: z.string().nullable(),
  penalty_anchor_date_str: z.string().nullable(),
  effective_lag_days: numNull(),
  has_timely_revision_before_initial_due: boolish(),
  is_declined: boolish(),
  po_declined_date_str: z.string().nullable(),
  has_no_portal_or_public_link_action: boolish(),
  fulfillment_penalty_status: z.string().nullable(),
  fulfillment_penalty_amount: numNull(),
  brand_dispatch_courier_partner_name: z.string().nullable(),
  brand_dispatch_courier_tracking_id: z.string().nullable(),
  brand_dispatch_courier_tracking_link: z.string().nullable(),
  vendor_dispatch_courier_partner_name: z.string().nullable(),
  vendor_dispatch_courier_tracking_id: z.string().nullable(),
  vendor_dispatch_courier_tracking_link: z.string().nullable(),
}));
export type MtoMerchHeadRawIssueLines = z.infer<typeof MtoMerchHeadRawIssueLinesSchema>;
